import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react';
import { _2faStatus } from '@interfaces/profile';
import { get2faStatus } from '@api/profile';
import { findPlatformUserAddress } from '@api/platformUserAddress';
import { PlatformUserAddress } from '@interfaces/platformUserAddress/platformUserAddress';

export const _2FA_BASE_URL = '/profile/2fa';

type User2faContextValue = {
  status: _2faStatus | null;
  setStatus: Dispatch<SetStateAction<User2faContextValue['status']>>;
  shouldRefetch: boolean;
  setShouldRefetch: Dispatch<SetStateAction<boolean>>;
  platformUserAddress: PlatformUserAddress | undefined;
  /** `true` até a primeira busca terminar — não se cobra verificação no escuro. */
  isLoading: boolean;
};

export const User2faContext = createContext<User2faContextValue>({
  status: null,
  setStatus: () => null,
  shouldRefetch: false,
  setShouldRefetch: () => false,
  platformUserAddress: undefined,
  isLoading: true,
});

type Props = PropsWithChildren;

/**
 * Provedor do status de verificação (e-mail, telefone e endereço). Só isso.
 *
 * **Não interrompe mais o cadastro.** Até 08/09/2026 era um portão: sem e-mail e telefone
 * confirmados a pessoa ia para `/profile/2fa` antes de ver qualquer tela, e sem endereço recebia um
 * modal que só deixava sair da conta. Nada disso é preciso para criar uma comunidade — é preciso
 * para receber, e é a tela de saque que cobra agora (ver `resolveWithdrawalVerification`).
 *
 * Rotear virou problema de quem cobra: `/profile/2fa` cuida do próprio `callbackUrl`.
 */
export const User2faContextProvider = (props: Props) => {
  const [address, setAddress] = useState<PlatformUserAddress>();
  const [status, setStatus] = useState<User2faContextValue['status']>(null);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetch2faStatus = async () => {
    try {
      const status = await get2faStatus();
      setStatus(status);

      const platformUserAddress = await findPlatformUserAddress();
      setAddress(platformUserAddress);
    } catch {
      // Consulta em segundo plano: preserva a última leitura sem interromper a tela.
      // A API continua responsável por validar qualquer solicitação de saque.
    } finally {
      setIsLoading(false);
      setShouldRefetch(false);
    }
  };

  useEffect(() => {
    fetch2faStatus();
  }, []);

  // Re-hidrata quando algum passo de verificação é concluído
  useEffect(() => {
    shouldRefetch && fetch2faStatus();
  }, [shouldRefetch]);

  return (
    <User2faContext.Provider
      value={{
        status,
        setStatus,
        shouldRefetch,
        setShouldRefetch,
        platformUserAddress: address,
        isLoading,
      }}
    >
      {props.children}
    </User2faContext.Provider>
  );
};
